import { CommonModule, NgStyle } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalFilterService } from '../../../../my-shared-lib/src/lib/shared-services/global-filter-service';
import { debounceTime, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { formatStat } from '../../../../my-shared-lib/src/lib/utils/format.utils';
import { CustomPayloadService } from '../shared/dashboard-payload-services/custom-payload.service';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [NgStyle, CommonModule, ReactiveFormsModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss'
})
export class NumberStatsComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  stat!: string;
  statControl = new FormControl('');
  private filterSub!: Subscription;
  private unSubscribe$: Subject<void> = new Subject();

  constructor(
    private configService: CustomPayloadService,
    private globalFilterService: GlobalFilterService,
    private http: HttpClient
  ) { }

  ngAfterViewInit(): void {
    this.loadStat(); // load initially without filters if any

    // Listen for direct stat updates (totalCost, simulatedPrice, simulatedMargin)
    if (['totalCost', 'simulatedPrice', 'simulatedMargin'].includes(this.data.key)) {
      this.globalFilterService.stats$
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((stats: any) => {
          const key = this.data.key;
          // React only if the stats object contains this key (even if null)
          if (stats && Object.prototype.hasOwnProperty.call(stats, key)) {
            const rawValue = stats[key];

            if (rawValue === null || rawValue === undefined || rawValue === '') {
              // Clear display when explicitly set to null/empty
              this.stat = '-';
              this.statControl.setValue('', { emitEvent: false });
            } else {
              this.stat = this.data?.useFormatStat
                ? formatStat(rawValue, this.data?.key || '', this.data?.prefix || '')
                : rawValue;
              this.statControl.setValue(this.stat, { emitEvent: false });
            }
          }
        });
    }

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          return this.loadStat(filters);
        })
      )
      .subscribe({
        next: (res: any) => {
          const parent = this.data?.parentKey ? res[this.data?.parentKey] : res;
          const rawValue = parent?.[this.data.key];

          this.stat = this.data?.useFormatStat
            ? formatStat(
                rawValue,
                this.data?.key || '',
                this.data?.prefix || ''
              )
            : rawValue;
          this.statControl.setValue(this.stat);
        },
        error: (err) => {
          console.error('Error loading stat:', err);
        },
      });

    // Handle input changes for backend calculation — removed debounced valueChanges
    // Calculation is only triggered by onEnterClick()
  }

  onEnterClick() {
    const value = this.statControl.value;
    if (value) {
      // Immediately publish own value and clear the opposite stat to avoid showing a stale value
      this.globalFilterService.updateStat(this.data.key, value);
      const otherKey = this.data.key === 'simulatedPrice' ? 'simulatedMargin' : 'simulatedPrice';
      this.globalFilterService.updateStat(otherKey, null);

      this.callApiWithSimulatedValue(value);
    }
  }

  private callApiWithSimulatedValue(value: string) {
    const tableData = this.globalFilterService.getTableData();
    const totals: any = {};
    
    if (this.data.key === 'simulatedPrice') {
      totals.simulated_price_2025 = value;
    } else if (this.data.key === 'simulatedMargin') {
      totals.simulated_margin_2025 = value;
    }

    const payload = {
      requestData: {
        source: "UI",
        quote_bom: tableData.map((item: any) => ({
          quote_id: item.quote_id || "QT-10001",
          product_number: item.product_number,
          product_name: item.product_name,
          cost_2025: item.cost_2025,
          price_2025: item.price_2025,
          cost_2024: item.cost_2024,
          price_2024: item.price_2024,
          cost_2023: item.cost_2023,
          price_2023: item.price_2023,
          is_active: item.is_active,
          uuid: item.uuid
        })),
        totals: totals
      },
      responseList: [],
      entity: "quote_bom",
      workflowId: "BOM_WORKFLOW_001",
      endpoints: [
        {
          functionName: "BomInsertUpdateService",
          version: "1.0"
        }
      ],
      communicationType: "rest",
      version: "1.0"
    };

    console.log('[NumberStats] Calling calculation API, key:', this.data.key, 'value:', value);
    this.http.post(this.data.calculationApiUrl || 'http://localhost:8085/spriced/platform', payload).subscribe({
      next: (response: any) => {
        console.log('[NumberStats] Calculation API response:', response);
        const otherKey = this.data.key === 'simulatedPrice' ? 'simulatedMargin' : 'simulatedPrice';

        // Extract value from multiple possible response paths
        let extractedValue: any;

        // 1. Try response.requestData.totals
        if (response?.requestData?.totals && this.data.responseKey) {
          extractedValue = response.requestData.totals[this.data.responseKey];
        }
        // 2. Try response.totals
        if ((extractedValue === undefined || extractedValue === null) && response?.totals && this.data.responseKey) {
          extractedValue = response.totals[this.data.responseKey];
        }
        // 3. Try root level
        if ((extractedValue === undefined || extractedValue === null) && response && this.data.responseKey) {
          extractedValue = response[this.data.responseKey];
        }
        // 4. Try responseList -> bom -> data
        if ((extractedValue === undefined || extractedValue === null) && Array.isArray(response?.responseList)) {
          try {
            const bomEntry = response.responseList[0]?.bom?.[0];
            const dataObj = bomEntry?.data || bomEntry;
            if (dataObj && this.data.responseKey in dataObj) {
              extractedValue = dataObj[this.data.responseKey];
            }
          } catch (e) { /* ignore */ }
        }

        console.log('[NumberStats] Extracted value:', extractedValue, 'for responseKey:', this.data.responseKey, '-> updating:', otherKey);

        if (extractedValue !== undefined && extractedValue !== null) {
          this.globalFilterService.updateStat(otherKey, extractedValue);
        }
      },
      error: (err) => {
        console.error('Error calling API:', err);
      }
    });
  }
  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }


  loadStat(filters?: any): any {
    return this.configService.getConfig(this.data?.apiUrl, {
      ...filters,
      useQueryParams: this.data?.useQueryParams || false
    })
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
